import { uploadWorkspaceArtifact } from './storageHubService'
import type { CSSProperties } from 'react'
import type { ChecklistItem } from '../types/schema'
import type { TimelineElement } from '../types/timelineElement'

export const CARD_BACKGROUND_ARTIFACT_TYPE = 'CARD_BACKGROUND'
export const CARD_BACKGROUND_CATEGORY_LABEL = 'card backgrounds'

const FALLBACK_COLORS = [
  '#f97316', // mosaic-keep: data (fallback card background hex color)
  '#22c55e', // mosaic-keep: data (fallback card background hex color)
  '#06b6d4', // mosaic-keep: data (fallback card background hex color)
  '#ec4899', // mosaic-keep: data (fallback card background hex color)
  '#eab308', // mosaic-keep: data (fallback card background hex color)
  '#14b8a6', // mosaic-keep: data (fallback card background hex color)
  '#ef4444', // mosaic-keep: data (fallback card background hex color)
  '#84cc16', // mosaic-keep: data (fallback card background hex color)
] as const

const isHexColor = (value: unknown): value is `#${string}` =>
  typeof value === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value)

const hashString = (value: string): number => {
  let hash = 0
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(index)) | 0
  }
  return Math.abs(hash)
}

export const deterministicCardColor = (seed: string): `#${string}` =>
  FALLBACK_COLORS[hashString(seed) % FALLBACK_COLORS.length]

export const resolveChecklistCardColor = (
  item: Pick<ChecklistItem, 'id' | 'parentContainerId'>,
  parentLookup?: ReadonlyMap<string, Pick<TimelineElement, 'id' | 'parentId' | 'color' | 'type'>>,
): `#${string}` => {
  let currentId: string | null | undefined = item.parentContainerId
  const visited = new Set<string>()
  while (currentId && !visited.has(currentId)) {
    visited.add(currentId)
    const foundParent: Pick<TimelineElement, 'id' | 'parentId' | 'color' | 'type'> | undefined = parentLookup?.get(currentId)
    if (!foundParent) break
    if (isHexColor(foundParent.color)) return foundParent.color
    currentId = foundParent.parentId
  }
  return deterministicCardColor(`${item.parentContainerId || 'root'}:${item.id}`)
}

export const uploadChecklistCardBackground = async (input: {
  workspaceId: string
  checklistItemId: string
  file: File
}) => {
  if (!input.file.type.startsWith('image/')) throw new Error('Choose an image file for the card background.')
  const uploaded = await uploadWorkspaceArtifact({
    workspaceId: input.workspaceId,
    file: input.file,
    visibility: 'WORKSPACE_SHARED',
    artifactType: CARD_BACKGROUND_ARTIFACT_TYPE,
    artifactId: input.checklistItemId,
  })
  return {
    cardBackgroundFileId: uploaded.fileId,
    cardBackgroundUrl: uploaded.downloadUrl,
    cardBackgroundStoragePath: uploaded.storagePath,
    cardBackgroundFileName: input.file.name,
  } satisfies Partial<ChecklistItem>
}

export const clearChecklistCardBackgroundUpdates = (): Partial<ChecklistItem> => ({
  cardBackgroundFileId: null,
  cardBackgroundUrl: null,
  cardBackgroundStoragePath: null,
  cardBackgroundFileName: null,
})

export const cardTextureVars = (
  item: Pick<ChecklistItem, 'id' | 'cardBackgroundUrl'>,
  accentColor: string,
  options: { includeImage?: boolean } = {},
): CSSProperties => {
  const textureIndex = hashString(item.id) % 5
  return {
    '--task-card-accent': accentColor,
    '--task-card-texture': String(textureIndex),
    ...(options.includeImage !== false && item.cardBackgroundUrl ? { '--task-card-image': `url("${item.cardBackgroundUrl}")` } : {}),
  } as CSSProperties
}
